import React, { useState, useEffect, useRef } from 'react';
import parseHtmltoString from '../../../helpers/utils/parseHtmlToString';

type Suggestion = {
  label: string;
  value: string;
};

interface AutoCompleteInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  suggestions: Suggestion[];
  onChange: (value) => void;
  required?: boolean;
  label?: string;
  labelClassName?: string;
  placeholder?: string;
  value?: string;
}

const AutoCompleteInput = ({
  suggestions,
  required,
  label,
  labelClassName,
  placeholder,
  value,
  ...props
}: AutoCompleteInputProps) => {
  const [query, setQuery] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState<Suggestion[]>([]);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseHtmltoString(event.target.value);
    setQuery(value);
    props.onChange(event);
    if (value.length > 0) {
      const filtered = suggestions.filter((suggestion) =>
        suggestion.label.toLowerCase().startsWith(value.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setDropdownVisible(filtered.length > 0);
    } else {
      setFilteredSuggestions([]);
      setDropdownVisible(false);
    }
  };

  const handleItemClick = (label: string) => {
    setQuery(label);
    setDropdownVisible(false);
    const syntheticEvent = {
      target: { value: label },
    } as React.ChangeEvent<HTMLInputElement>;

    props.onChange(syntheticEvent);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const renderSuggestion = (suggestion: Suggestion) => {
    const matchStart = suggestion.label.toLowerCase().indexOf(query.toLowerCase());
    const matchEnd = matchStart + query.length;

    return (
      <div
        key={suggestion.value}
        className="px-4 py-2 cursor-pointer hover:bg-gray-100"
        onClick={() => handleItemClick(suggestion.label)}
      >
        {matchStart >= 0 ? (
          <>
            {suggestion.label.substring(0, matchStart)}
            <span className="font-bold">{suggestion.label.substring(matchStart, matchEnd)}</span>
            {suggestion.label.substring(matchEnd)}
          </>
        ) : (
          suggestion.label
        )}
      </div>
    );
  };

  return (
    <>
      <div className="relative">
        {label && (
          <label className={labelClassName}>
            {label}
            {required && <span className="text-red-500"> *</span>}
          </label>
        )}
        <div ref={autocompleteRef} className="relative w-full">
          <input
            type="text"
            value={query || value}
            onChange={handleInputChange}
            placeholder={placeholder}
            className={`w-full outline-none bg-reliableBlack3 text-reliableBlack border-0 border-b px-5 py-4 border-reliableBlack30 placeholder:text-reliableBlack60 focus:border-reliableBlack60 mt-2`}
          />
          {isDropdownVisible && (
            <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {filteredSuggestions.map((suggestion) => renderSuggestion(suggestion))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AutoCompleteInput;
